import React, { FC, FocusEvent, KeyboardEvent } from 'react';
import { Selector } from '@components/FormInputs/Selector';
import { RadioButton } from '@components/FormInputs/RadioButton';
import { useUniqueId } from '@hooks/useUniqueId';
import { PassReqFactory } from './PassReqFactory';
import { TextInputFactory } from './TextInputFactory';
import { DatePickerFactory } from './DatePickerFactory';
import { CheckboxFactory } from './CheckboxFactory';
import {
  QuestionContainer,
  TitleText,
  HelpText,
  LegalText,
  HiddenLabel,
} from './Question.elements';

export const INPUT_TYPE_SELECTOR = 'selector';
export const INPUT_TYPE_BOOLEAN = 'boolean';
export const INPUT_TYPE_RADIO = 'radio';
export const INPUT_TYPE_NUMBER = 'number';
export const INPUT_TYPE_PASSWORD = 'password';
export const INPUT_TYPE_PASSWORD_REQUIREMENTS = 'password-requirements';
export const INPUT_TYPE_TEL = 'tel';
export const INPUT_TYPE_TEXT = 'text';
export const INPUT_TYPE_CHECKBOX = 'checkbox';
export const INPUT_TYPE_RANGE = 'range';
export const INPUT_DATE_PICKER = 'datepicker';
export const INPUT_CHECKBOX = 'checkbox';

const labelPrefix = 'question-label-';

export type Props = {
  value: string;
  error: boolean;
  inputData: QuestionDataInput;
  onAnswerQuestion: (name: string, value: any) => void;
  id?: string;
  onBlur?: (event: FocusEvent<any>) => void
  className?: string
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
}

export const Question: FC<Props> = (props) => {
  const uniqueId = props.id || useUniqueId(labelPrefix)
  return (
    <QuestionContainer className={props.className}>
      {props.inputData.title ? (
        <TitleText
          htmlFor={uniqueId}
          isRequired={!props.inputData.optional}
        >
          {props.inputData.title}
        </TitleText>
      ) : (
        <HiddenLabel htmlFor={uniqueId}>{props.inputData.placeholder}</HiddenLabel>
      )}
      <QuestionInput {...props} id={uniqueId} />
      {props.inputData.helpText && (
        <HelpText>{props.inputData.helpText}</HelpText>
      )}
      {props.inputData.legalText && (
        <LegalText dangerouslySetInnerHTML={{ __html: props.inputData.legalText }} />
      )}
    </QuestionContainer>
  );
}

const SelectorFactory: FC<Props> = (props) => {
  const { value, onAnswerQuestion, error, id } = props;
  const { key, options, placeholder, dataType } = props.inputData;

  if (!options) return null

  return (
    <Selector
      id={id}
      name={key}
      value={value}
      dataType={dataType}
      onChange={onAnswerQuestion}
      options={options}
      placeholder={placeholder}
      error={error}
    />
  );
}

const RadioButtonFactory: FC<Props> = (props) => {
  const { value, onAnswerQuestion, error } = props;
  const { key, options, displaySlim } = props.inputData;

  if (!options) return null

  return (
    <RadioButton
      name={key}
      value={value}
      onChange={onAnswerQuestion}
      options={options}
      error={error}
      displaySlim={displaySlim}
    />
  );
}

const QuestionInput: FC<Props> = (props) => {
  const { inputData } = props;
  switch (inputData.type) {
    case INPUT_TYPE_SELECTOR:
      return (<SelectorFactory {...props} />);
    case INPUT_TYPE_TEXT:
    case INPUT_TYPE_TEL:
    case INPUT_TYPE_NUMBER:
    case INPUT_TYPE_PASSWORD:
      return (<TextInputFactory {...props} />);
    case INPUT_TYPE_PASSWORD_REQUIREMENTS:
      return (<PassReqFactory {...props} />);
    case INPUT_TYPE_RADIO:
      return (<RadioButtonFactory {...props} />);
    case INPUT_DATE_PICKER:
      return (<DatePickerFactory {...props} />);
    case INPUT_CHECKBOX:
      return (<CheckboxFactory {...props} />);
    default:
      return (<p>type not recognized: {inputData.type}</p>);
  }
}
